import React from "react";
import NewStaffDialog from "./components/NewStaffDialog.tsx";
import { Link } from "react-router-dom";
import Loading from "../../Loading";
import StopUserButton from "../../components/StopUserButton";
import Salaries from "./components/Salaries.tsx";
import { UserElement } from "./types.ts";
import Error from "../../components/Error.jsx";
import mut from "../../hooks/mut.ts";
import DateChoice from "../../components/DateChoice.jsx";
import useDBO from "../../hooks/useDBO.ts";
import ChangeManager from "./components/ChangeManager.tsx";
import { Users, Plus, Sparkles, Loader2, ArrowRightLeft, Calendar } from "lucide-react";
import Lang from "../../components/Lang.jsx";

/**
 * Staff — Main staff management page at /manage/staff.
 * Displays a list of the manager's team members as horizontal row cards
 * with inline salary editing, review dates, and management actions.
 *
 * @author Forge (opencode + glm-5.1)
 * @date 2026-04-16
 * @version 2.0
 * @returns {React.ReactElement}
 */
export default function Staff() {
  const [openNSD, setOpenNSD] = React.useState(false);
  const [selectedStaff, setSelectedStaff] = React.useState<UserElement | undefined>(undefined);
  const [openManagerDialog, setOpenManagerDialog] = React.useState(false);
  const myStaff = useDBO<UserElement>(`/mystaff`);

  if (myStaff.loading) {
    return (
    <div className="p-6 max-w-[1600px] mx-auto h-full bg-[#f8fafc] text-[#1e293b] flex flex-col">
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Loader2 size={32} className="animate-spin text-slate-300" />
          <p className="text-slate-400"><Lang w="loading" /></p>
        </div>
      </div>
    );
  }

  if (myStaff.error) return <Error />;

  const staffList = myStaff.get || [];

  /**
   * Extract initials from a staff member's name for the avatar circle.
   * @param {string} name - Full name
   * @returns {string} 1-2 character initials
   */
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  /**
   * Deterministic avatar background color from a string.
   * @param {string} str - Input string
   * @returns {string} Tailwind bg class
   */
  const avatarColor = (str: string) => {
    const colors = [
      "bg-sky-100 text-sky-600",
      "bg-emerald-100 text-emerald-600",
      "bg-violet-100 text-violet-600",
      "bg-amber-100 text-amber-600",
      "bg-rose-100 text-rose-600",
      "bg-indigo-100 text-indigo-600",
      "bg-teal-100 text-teal-600",
      "bg-orange-100 text-orange-600",
    ];
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto min-h-screen bg-[#f8fafc] text-[#1e293b]">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 pb-3 border-b border-[#e2e8f0]">
        <h1 className="text-[2rem] font-extrabold text-teal-600 tracking-tight m-0 flex items-center gap-3">
          <Users size={28} />
          My Staff
        </h1>
        <div className="flex items-center gap-3">
          {staffList.length > 0 && (
            <span className="bg-teal-50 text-teal-600 px-4 py-1.5 rounded-full text-sm font-bold">
              {staffList.length} member{staffList.length !== 1 ? "s" : ""}
            </span>
          )}
          <button
            onClick={() => setOpenNSD(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl font-bold text-sm hover:bg-teal-700 transition-all duration-150 cursor-pointer shadow-sm"
            title="Add new staff member"
          >
            <Plus size={16} strokeWidth={2.5} />
            Add Staff
          </button>
        </div>
      </div>

      {/* Empty State */}
      {staffList.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-12 text-center flex flex-col items-center gap-3">
          <Sparkles size={40} className="text-slate-300 mb-2" />
          <p className="text-slate-400 text-lg font-medium">No staff members yet</p>
          <p className="text-slate-400 text-sm">Click "Add Staff" above to invite your first team member.</p>
        </div>
      )}

      {/* Staff Cards */}
      {staffList.length > 0 && (
        <div className="flex flex-col gap-3 flex-1 overflow-y-auto">
          {myStaff.map((member) => (
            <div
              key={member._id}
              className="bg-white border border-slate-200 rounded-2xl shadow-sm px-5 py-4 transition-all duration-200 hover:shadow-md flex items-center gap-6"
            >
              {/* Avatar + Name */}
              <div className="flex items-center gap-3 min-w-[200px]">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(member.name)}`}
                >
                  {getInitials(member.name)}
                </div>
                <Link
                  to={`/manage/staff/${member._id}`}
                  className="text-sm font-bold text-slate-800 hover:text-teal-600 transition-colors truncate"
                >
                  {member.name}
                </Link>
              </div>

              {/* Salary */}
              <div className="flex-shrink-0">
                <Salaries member={member} myStaff={myStaff} />
              </div>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Review Date */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Calendar size={14} className="text-slate-400 flex-shrink-0" />
                <DateChoice
                  value={member.nextReview}
                  setValue={async (nextReview: string) => {
                    const newUser = await mut(`/user/${member._id}`, { nextReview });
                    myStaff.update(newUser);
                  }}
                  label="Next review"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => {
                    setSelectedStaff(member);
                    setOpenManagerDialog(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-600 border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-100 hover:border-slate-300 active:bg-slate-200 transition-all duration-150 cursor-pointer"
                  title="Change manager"
                >
                  <ArrowRightLeft size={14} />
                  Manager
                </button>

                <StopUserButton
                  id={member._id}
                  result={(res: UserElement) => myStaff.remove(res._id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <NewStaffDialog open={openNSD} setOpen={setOpenNSD} myStaff={myStaff} />

      {selectedStaff && (
        <ChangeManager
          open={openManagerDialog}
          setOpen={setOpenManagerDialog}
          user={selectedStaff}
          myStaff={myStaff}
        />
      )}
    </div>
  );
}
